import { createSelector } from 'reselect';

import { MOET_CONSTANTS } from 'moet-core';

import { FRESH_CHAT_GROUP_IDS, QUEUE_NAME } from '@/constants/fresh-chat';
import { IS_ONBOARDING_APP, FEATURES } from '@/constants';

import { appState } from './utils';
import { getMerchant, getMerchantCountryKey, merchantRegion } from './merchant/common';
import { getWidgetTypeFromWidgetCookie } from './widget';
import { getSetupData } from './data';
import { getFeatureConfig } from './featuresConfig';
import { addMilliseconds, isWithinInterval, startOfDay, addSeconds } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';

const { ONBOARDING_STATUS_VALUES_V2, WIDGET_TYPE } = MOET_CONSTANTS;

export const isWorkingHours = createSelector(
  appState,
  getMerchantCountryKey,
  ({ freshChat: { operatingHours } = {} }, merchantCountry) => {
    // We need to rely on user local machine time to be synced AND __in correct timezone__
    // otherwise this becomes a guessing game and there's no correct answer
    // The number of minutes returned by getTimezoneOffset() is positive if the local time zone
    // is behind, meaning that this function will return negative values for timezones that are
    // east of UTC. For consistency with date-fns-tz we need to invert this.
    const localOffsetMs = new Date().getTimezoneOffset() * 60 * 1000 * -1;
    const freshChatOffsetMs = getTimezoneOffset(operatingHours.timezone);
    const diffOffsetMs = freshChatOffsetMs - localOffsetMs;

    // After this the time and date is representing current date as if in target timezone
    const dt = addMilliseconds(new Date(), diffOffsetMs);

    // Monday for FreshChat is 0, we need to roll the value right by 1 to match with JS date
    const freshChatDay = (dt.getDay() + 1) % 7;

    const isWorkingDay = (operatingHours.working || {})[freshChatDay];

    if (isWorkingDay === 'false' || !isWorkingDay) {
      return false;
    }

    // start and end hour data format: `"0": "0;3600;28800;61200;"`
    const operatingDateTimes = operatingHours.days[freshChatDay]
      .split(';')
      .slice(0, -1)
      .map(value => addSeconds(startOfDay(dt), Number(value)));

    for (let i = 0; i < operatingDateTimes.length; i += 2) {
      const start = operatingDateTimes[i];
      const end = operatingDateTimes[i + 1];

      if (isWithinInterval(dt, { start, end })) {
        return true;
      }
    }

    return false;
  },
);

export const getQueueName = createSelector(getMerchant, merchant => {
  const onboardingStatus = ONBOARDING_STATUS_VALUES_V2[merchant.status];
  if (
    onboardingStatus > ONBOARDING_STATUS_VALUES_V2.TRANSACTION_DETAILS_STARTED &&
    onboardingStatus < ONBOARDING_STATUS_VALUES_V2.COMPLETED
  ) {
    return QUEUE_NAME.REVIEW;
  }

  if (onboardingStatus === ONBOARDING_STATUS_VALUES_V2.COMPLETED) {
    return QUEUE_NAME.POST_APPROVAL;
  }

  return QUEUE_NAME.ONBOARDING;
});

export const shouldShowChat = createSelector(
  getMerchant,
  merchantRegion,
  getWidgetTypeFromWidgetCookie,
  getFeatureConfig(FEATURES.SHOW_FRESH_CHAT),
  getQueueName,
  (merchant, region, widgetType, chatFeatureConfig = {}, queueName) => {
    const allCountryQueues = chatFeatureConfig[merchant.country];
    const hasQueueEnabled = allCountryQueues?.includes(queueName);
    // check if the feature toggle is on, country is in the list and chat queue is enabled
    const isChatEnabledForCountry =
      chatFeatureConfig.is_enabled && !!allCountryQueues && hasQueueEnabled;
    const showChatOnSignup = ['us', 'ap', 'oc'].includes(region) && !merchant.id;

    return (
      (IS_ONBOARDING_APP || widgetType === WIDGET_TYPE.UNMANAGED_KYC) &&
      (isChatEnabledForCountry || showChatOnSignup)
    );
  },
);

export const getGroupId = createSelector(
  getMerchant,
  getSetupData,
  merchantRegion,
  getQueueName,
  (merchant, setupData, region, queueName) => {
    // we do not have merchant country on the fisrt step of the onboarding flow
    let countryForSignupFlow;
    if (region === 'us') countryForSignupFlow = 'US';
    if (['ap', 'oc'].includes(region)) countryForSignupFlow = 'AU';

    const country = merchant?.country || setupData?.country || countryForSignupFlow;
    return FRESH_CHAT_GROUP_IDS[country]?.[queueName];
  },
);
